import { useEffect, useMemo, useRef, useState } from 'react';

import {
    type AxisConfiguration,
    buildAxisPreview,
    clampAxisConfigurationToLoadedCount,
    configsAreEqual,
    createDefaultAxisConfiguration,
} from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import { getOtherGroupLabel } from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import { useAxisConfigurationPreview } from 'components/molecules/PackArchitecture/useAxisConfigurationPreview';
import { getAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { useWhitespaceAxisConfigurationPreview } from 'hooks/useWhitespaceAxisConfigurationPreview';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';

export const withoutExcludedControls = (
    config: AxisConfiguration,
    controlsMeta?: AxisGroupByControlsMeta | null,
    hideGroupRestAsOther = false,
    totalCount = config.showTop,
): AxisConfiguration => ({
    ...config,
    showTop: controlsMeta?.showTop ? config.showTop : totalCount,
    groupRestAsOther: hideGroupRestAsOther || !controlsMeta?.showTop ? false : config.groupRestAsOther,
    showOwnVendorFirst: controlsMeta?.ownDimensionSort ? config.showOwnVendorFirst : false,
});

const buildAppliedChartConfig = ({
    initialConfig,
    totalCount,
    configurationDefaults,
    controlsMeta,
    hideGroupRestAsOther,
}: {
    initialConfig?: AxisConfiguration;
    totalCount: number;
    configurationDefaults: ReturnType<typeof getAxisConfigurationDefaultsFromMeta>;
    controlsMeta?: AxisGroupByControlsMeta | null;
    hideGroupRestAsOther: boolean;
}): AxisConfiguration => {
    const defaultConfig = withoutExcludedControls(
        createDefaultAxisConfiguration(totalCount, configurationDefaults),
        controlsMeta,
        hideGroupRestAsOther,
        totalCount,
    );
    const base = initialConfig ? clampAxisConfigurationToLoadedCount(initialConfig, totalCount) : defaultConfig;
    return withoutExcludedControls(base, controlsMeta, hideGroupRestAsOther, totalCount);
};

type UseAxisConfigurationModalStateParams = {
    open: boolean;
    dimensionLabel: string;
    controlsMeta?: AxisGroupByControlsMeta | null;
    initialConfig?: AxisConfiguration;
    chartRequest?: PricePackArchitectureRequest | null;
    whitespacePreviewRequest?: Record<string, unknown> | null;
    useWhitespacePreview?: boolean;
    whitespacePreviewAxisKind?: 'column' | 'row';
    hideGroupRestAsOther?: boolean;
    xAxis?: string;
    apiTotalCount?: number;
    onClose: () => void;
    onApply?: (config: AxisConfiguration) => void;
};

/** Encapsulates all state, derived values, and handlers for `AxisConfigurationModal`, keeping the component itself focused on JSX. */
export const useAxisConfigurationModalState = ({
    open,
    dimensionLabel,
    controlsMeta,
    initialConfig,
    chartRequest = null,
    whitespacePreviewRequest = null,
    useWhitespacePreview = false,
    whitespacePreviewAxisKind = 'column',
    hideGroupRestAsOther = false,
    xAxis = '',
    apiTotalCount = 0,
    onClose,
    onApply,
}: UseAxisConfigurationModalStateParams) => {
    const configurationDefaults = useMemo(() => getAxisConfigurationDefaultsFromMeta(controlsMeta), [controlsMeta]);

    const initialAppliedConfig = useMemo(
        () =>
            buildAppliedChartConfig({
                initialConfig,
                totalCount: apiTotalCount,
                configurationDefaults,
                controlsMeta,
                hideGroupRestAsOther,
            }),
        [apiTotalCount, configurationDefaults, controlsMeta, hideGroupRestAsOther, initialConfig],
    );

    const [config, setConfig] = useState<AxisConfiguration>(initialAppliedConfig);
    const [baselineConfig, setBaselineConfig] = useState<AxisConfiguration>(initialAppliedConfig);
    const [pauseShowTopPreview, setPauseShowTopPreview] = useState(false);
    const [stableTotalCount, setStableTotalCount] = useState(0);
    const prevOpenRef = useRef(false);

    const showOwnEntityFirstOption = Boolean(controlsMeta?.ownDimensionSort) && !hideGroupRestAsOther;

    const ppaPreview = useAxisConfigurationPreview({
        open: open && !useWhitespacePreview,
        chartRequest: chartRequest ?? null,
        xAxis,
        previewConfig: config,
        pauseFetch: pauseShowTopPreview,
    });

    const whitespacePreview = useWhitespaceAxisConfigurationPreview({
        open: open && useWhitespacePreview,
        gridRequest: whitespacePreviewRequest,
        previewConfig: config,
        pauseFetch: pauseShowTopPreview,
        axisKind: whitespacePreviewAxisKind,
    });

    const {
        previewItems,
        isLoading: isPreviewLoading,
        showTopStats,
    } = useWhitespacePreview ? whitespacePreview : ppaPreview;

    useEffect(() => {
        if (!open) {
            setStableTotalCount(0);
            return;
        }

        const nextTotal = Math.max(apiTotalCount, whitespacePreview.totalCount);
        if (nextTotal > 0) {
            setStableTotalCount((current) => Math.max(current, nextTotal));
        }
    }, [apiTotalCount, open, whitespacePreview.totalCount]);

    const effectiveTotalCount = useWhitespacePreview
        ? Math.max(stableTotalCount, apiTotalCount, whitespacePreview.totalCount)
        : apiTotalCount;

    const defaultConfig = useMemo(
        () =>
            buildAppliedChartConfig({
                totalCount: effectiveTotalCount,
                configurationDefaults,
                controlsMeta,
                hideGroupRestAsOther,
            }),
        [configurationDefaults, controlsMeta, effectiveTotalCount, hideGroupRestAsOther],
    );

    const appliedChartConfig = useMemo(
        () =>
            buildAppliedChartConfig({
                initialConfig,
                totalCount: effectiveTotalCount,
                configurationDefaults,
                controlsMeta,
                hideGroupRestAsOther,
            }),
        [configurationDefaults, controlsMeta, effectiveTotalCount, hideGroupRestAsOther, initialConfig],
    );

    useEffect(() => {
        const justOpened = open && !prevOpenRef.current;
        prevOpenRef.current = open;
        if (justOpened) {
            setConfig(appliedChartConfig);
            setBaselineConfig(appliedChartConfig);
            setPauseShowTopPreview(false);
        }
    }, [open, appliedChartConfig]);

    useEffect(() => {
        if (!open || !useWhitespacePreview || initialConfig || effectiveTotalCount <= 0 || isPreviewLoading) {
            return;
        }

        setConfig((current) => {
            if (current.showTop > 0 && current.showTop <= effectiveTotalCount) {
                return current;
            }
            return clampAxisConfigurationToLoadedCount(
                { ...current, showTop: effectiveTotalCount },
                effectiveTotalCount,
            );
        });
    }, [effectiveTotalCount, initialConfig, isPreviewLoading, open, useWhitespacePreview]);

    const hasApplyChanges = useMemo(() => !configsAreEqual(config, baselineConfig), [baselineConfig, config]);

    const hasResetChanges = useMemo(() => !configsAreEqual(config, defaultConfig), [config, defaultConfig]);

    const previewResult = useMemo(
        () =>
            buildAxisPreview(previewItems, config, getOtherGroupLabel(dimensionLabel, controlsMeta?.pluralLabel), {
                preserveApiOrder: true,
                showTopStats,
            }),
        [config, controlsMeta?.pluralLabel, dimensionLabel, previewItems, showTopStats],
    );

    const updateConfig = (patch: Partial<AxisConfiguration>) => {
        setConfig((current) => ({ ...current, ...patch }));
    };

    const handleReset = () => {
        setConfig(defaultConfig);
    };

    const handleCancel = () => {
        setConfig(baselineConfig);
        onClose();
    };

    const handleApply = () => {
        setBaselineConfig(config);
        onApply?.(withoutExcludedControls(config, controlsMeta, hideGroupRestAsOther, effectiveTotalCount));
        onClose();
    };

    return {
        config,
        baselineConfig,
        effectiveTotalCount,
        isPreviewLoading,
        pauseShowTopPreview,
        setPauseShowTopPreview,
        showOwnEntityFirstOption,
        hasApplyChanges,
        hasResetChanges,
        previewResult,
        updateConfig,
        handleReset,
        handleCancel,
        handleApply,
    };
};
